/**
 * Los totales del carrito del POS, en un solo sitio.
 *
 * Existía tres veces en el mismo flujo y una de ellas estaba mal: la barra
 * inferior del dispensario (`Dispenser.jsx`) sumaba precio × cantidad y llamaba
 * «Total» a eso, sin descuentos. Con un socio al 50% la barra decía «Total:
 * €10.00» y el panel cobraba €5.00 (reporte 29-08-2026). Peor: `CartBar` avisa
 * de saldo insuficiente comparando contra el total que recibe, así que con esa
 * cifra inflada le decía al camarero que el socio quedaba en negativo cuando le
 * llegaba de sobra.
 *
 * El descuento se aplica POR LÍNEA, no sobre el subtotal: el manual es de la
 * línea y el del socio es del socio, y sumados se topan al 100% — sin el tope,
 * un 60% manual sobre un socio al 50% devolvería dinero.
 */

const clamp01 = (n) => Math.min(1, Math.max(0, n));

const numero = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
};

/**
 * A céntimos, y no es cosmética. `CartBar` decide el aviso de saldo con
 * `total > credit`, `CartFooter` con `saldo - total < 0` y `PaymentModal` igual:
 * sin redondear, 6 líneas de 1 € con 50% de socio y 10% manual dan
 * 2.4000000000000004, que es mayor que un saldo de 2,40 € — y la barra pinta
 * «€2.40» mientras avisa en rojo de que no llega. Lo mismo sin ruido de coma
 * flotante en cuanto hay medios céntimos reales (5 × 1,25 € al 15% = 5,3125).
 * El dinero se cobra en céntimos, así que aquí se decide en céntimos.
 */
const aCentimos = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * @param {Array<{price:number, quantity:number, manual_discount?:number}>} items
 *   UNA sola grafía a propósito (revisión max 04-09): quien venga de la Sala o
 *   de comandas (`unit_price`/`discount_pct`) traduce en SU frontera con un
 *   .map() — dos grafías aquí crearían precedencias silenciosas si una línea
 *   trajera ambas.
 * @param {number} memberRate Descuento del socio en tanto por uno (0.5 = 50%).
 *   Sin socio seleccionado va 0: el carrito pendiente no tiene descuento de socio.
 * @returns {{subtotal:number, descuentoSocio:number, descuentoManual:number, descuento:number, total:number}}
 */
// spec: saldo-pagos-caja#descuentos-y-total
// «Manual y socio se ENCADENAN, no se suman» · «El descuento se aplica por LÍNEA,
// nunca sobre el subtotal» · «El total se decide en céntimos».
export function calcularTotalesCarrito(items = [], memberRate = 0) {
  const socioRate = clamp01(numero(memberRate));

  let subtotalBruto = 0;
  let manualBruto = 0;
  let descuentoBruto = 0;

  // `items` llega de `JSON.parse(localStorage…)` en dos sitios del POS sin
  // comprobar que sea un array, y esta función corre en render en cuatro. Un
  // `{}` guardado por una versión vieja tumbaría la pantalla entera.
  for (const item of Array.isArray(items) ? items : []) {
    const lineaBruta = numero(item?.price) * numero(item?.quantity);
    subtotalBruto += lineaBruta;

    const manualRate = clamp01(numero(item?.manual_discount) / 100);

    // ENCADENADOS, no sumados (31-08-2026, y el backend hace lo mismo): primero el
    // manual sobre la línea, y sobre el precio YA rebajado se aplica el del socio.
    // Sumarlos hacía que un `60` sobre un socio con 50% en ficha saliera GRATIS en vez
    // de al 80% — el camarero pedía un descuento y el club regalaba el producto.
    const trasManual = lineaBruta * (1 - manualRate);
    manualBruto += lineaBruta - trasManual;
    descuentoBruto += lineaBruta - trasManual * (1 - socioRate);
  }

  // El SOCIO se deriva, para que las dos líneas del desglose sumen exactamente el
  // descuento que se resta del subtotal; redondearlas por separado dejaba un céntimo
  // bailando entre el desglose y el total. Antes se derivaba el manual — ahora el
  // manual es el que se conoce exacto (es lo que tecleó el camarero) y el del socio
  // es el que cae sobre el resto.
  const subtotal = aCentimos(subtotalBruto);
  const descuento = aCentimos(descuentoBruto);
  const descuentoManual = aCentimos(manualBruto);

  return {
    subtotal,
    descuentoSocio: aCentimos(descuento - descuentoManual),
    descuentoManual,
    descuento,
    total: aCentimos(Math.max(0, subtotal - descuento)),
  };
}
